<template>
  <div class="drivers-page space-y-5">
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">Drivers</h1>
        <p class="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">{{ filteredDrivers.length }} driver{{ filteredDrivers.length !== 1 ? 's' : '' }}</p>
      </div>
      <button @click="openCreateModal" class="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-primary-700 text-white hover:bg-primary-800 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2">
        <span class="material-icons text-[18px]" aria-hidden="true">person_add</span>
        Add Driver
      </button>
    </div>

    <ErrorState v-if="fetchError && !filteredDrivers.length" title="Couldn't load drivers" :message="fetchError" @retry="fetchDrivers" />
    <template v-else>
    <AlertBanner v-if="fetchError" type="error" message="Couldn't refresh drivers — showing the last loaded list." :dismissible="false" class="mb-4" />
    <DataTable :data="filteredDrivers" :columns="tableColumns" :loading="isLoading" :searchable="true" :pagination="true" loading-text="Loading drivers..." empty-text="No drivers found">
      <template #cell-name="{ row }">
        <div class="flex items-center gap-2">
          <div class="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" :class="row.active !== false ? 'bg-primary-50 dark:bg-primary-900/20' : 'bg-neutral-100 dark:bg-neutral-700'">
            <span class="text-xs font-bold" :class="row.active !== false ? 'text-primary-700 dark:text-primary-300' : 'text-neutral-400'">{{ row.firstName?.charAt(0)?.toUpperCase() }}</span>
          </div>
          <span class="text-sm font-medium text-neutral-900 dark:text-neutral-100">{{ row.firstName }} {{ row.lastName }}</span>
        </div>
      </template>

      <template #cell-active="{ row }">
        <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold" :class="row.active !== false ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-neutral-200 text-neutral-500 dark:bg-neutral-700 dark:text-neutral-400'">
          <span class="inline-block w-1.5 h-1.5 rounded-full" :class="row.active !== false ? 'bg-emerald-500' : 'bg-neutral-400'"></span>
          {{ row.active !== false ? 'Active' : 'Inactive' }}
        </span>
      </template>

      <template #actions="{ row }">
        <div class="flex items-center gap-1 justify-center">
          <button @click="openEditModal(row)" class="p-1.5 rounded-lg text-neutral-500 dark:text-neutral-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:text-primary-400 dark:hover:bg-primary-900/30 transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500" title="Edit" aria-label="Edit driver">
            <span class="material-icons text-[16px]" aria-hidden="true">edit</span>
          </button>
          <button @click="toggleActive(row)" class="p-1.5 rounded-lg text-neutral-500 dark:text-neutral-400 transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500" :class="row.active !== false ? 'hover:text-amber-600 hover:bg-amber-50 dark:hover:text-amber-400 dark:hover:bg-amber-900/30' : 'hover:text-emerald-600 hover:bg-emerald-50 dark:hover:text-emerald-400 dark:hover:bg-emerald-900/30'" :title="row.active !== false ? 'Deactivate' : 'Activate'" :aria-label="row.active !== false ? 'Deactivate driver' : 'Activate driver'">
            <span class="material-icons text-[16px]" aria-hidden="true">{{ row.active !== false ? 'pause_circle' : 'play_circle' }}</span>
          </button>
          <button @click="confirmDeleteDriver(row)" class="p-1.5 rounded-lg text-neutral-500 dark:text-neutral-400 hover:text-red-600 hover:bg-red-50 dark:hover:text-red-400 dark:hover:bg-red-900/30 transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500" title="Delete" aria-label="Delete driver">
            <span class="material-icons text-[16px]" aria-hidden="true">delete_outline</span>
          </button>
        </div>
      </template>
    </DataTable>
    </template>

    <!-- Create/Edit Modal -->
    <Modal :show="showModal" :title="isEditing ? 'Edit Driver' : 'Add Driver'" size="md" @close="closeModal">
      <form id="driver-form" @submit.prevent="submitDriver" class="space-y-4">
        <div>
          <label for="drv-employee" class="block text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1.5">Employee Number</label>
          <input id="drv-employee" v-model="form.employeeNumber" type="text" required :disabled="isEditing" placeholder="e.g. EMP-1042"
            class="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50" />
        </div>
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label for="drv-first-name" class="block text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1.5">First Name</label>
            <input id="drv-first-name" v-model="form.firstName" type="text" required placeholder="First name"
              class="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
          </div>
          <div>
            <label for="drv-last-name" class="block text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1.5">Last Name</label>
            <input id="drv-last-name" v-model="form.lastName" type="text" required placeholder="Last name"
              class="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
          </div>
        </div>
        <div>
          <label for="drv-pin" class="block text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1.5">Password / PIN</label>
          <div class="relative">
            <input id="drv-pin" v-model="form.password" :type="showPin ? 'text' : 'password'" autocomplete="new-password" minlength="4" :required="!isEditing" placeholder="Min 4 characters"
              class="w-full px-3 py-2 pr-10 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
            <button type="button" @click="showPin = !showPin" :title="showPin ? 'Hide PIN' : 'Show PIN'" :aria-label="showPin ? 'Hide PIN' : 'Show PIN'"
              class="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500">
              <span class="material-icons text-[18px]" aria-hidden="true">{{ showPin ? 'visibility_off' : 'visibility' }}</span>
            </button>
          </div>
          <p v-if="isEditing" class="text-[11px] text-neutral-500 dark:text-neutral-400 mt-1">Leave blank to keep existing</p>
        </div>
        <div v-if="formError" class="flex items-center gap-2 px-3 py-2.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded-lg">
          <span class="material-icons text-red-500 text-sm" aria-hidden="true">error</span>
          <p class="text-xs text-red-700 dark:text-red-400">{{ formError }}</p>
        </div>
      </form>
      <template #footer>
        <div class="flex items-center justify-end gap-2">
          <button @click="closeModal" class="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-700 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-600 transition-colors">Cancel</button>
          <button type="submit" form="driver-form" :disabled="isSubmitting" class="px-4 py-2 text-sm font-semibold text-white bg-primary-700 rounded-lg hover:bg-primary-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            {{ isSubmitting ? 'Saving...' : (isEditing ? 'Update' : 'Create') }}
          </button>
        </div>
      </template>
    </Modal>

    <!-- Delete Confirm -->
    <Modal :show="showDeleteConfirm" title="Delete Driver" size="sm" @close="showDeleteConfirm = false">
      <div class="bg-red-50 dark:bg-red-900/15 border border-red-200 dark:border-red-800/40 rounded-lg p-4 space-y-2">
        <p class="text-sm text-red-700 dark:text-red-300">Delete <strong>{{ driverToDelete?.firstName }} {{ driverToDelete?.lastName }}</strong>?</p>
        <p class="text-xs text-amber-600 dark:text-amber-400">Consider deactivating instead to preserve historical data.</p>
      </div>
      <template #footer>
        <div class="flex items-center justify-end gap-2">
          <button @click="showDeleteConfirm = false" class="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-700 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-600 transition-colors">Cancel</button>
          <button @click="deleteDriver" :disabled="isSubmitting" class="px-4 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">{{ isSubmitting ? 'Deleting...' : 'Delete' }}</button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import { ref, onMounted } from "vue";
import { useStore } from "vuex";
import axios from "axios";
import DataTable from "@/components/ui/DataTable.vue";
import Modal from "@/components/ui/Modal.vue";
import ErrorState from "@/components/ui/ErrorState.vue";
import AlertBanner from "@/components/ui/AlertBanner.vue";
import useToast from "@/composables/useToast";

export default {
  name: "DriversView",
  components: { DataTable, Modal, ErrorState, AlertBanner },
  setup() {
    const store = useStore();
    const apiUrl = store.state.apiUrl;
    store.commit("setCurrentRoute", "Drivers");
    const { showToast } = useToast();

    const filteredDrivers = ref([]);
    const isLoading = ref(false);
    const fetchError = ref(null);
    const showPin = ref(false);
    const showModal = ref(false);
    const showDeleteConfirm = ref(false);
    const isEditing = ref(false);
    const isSubmitting = ref(false);
    const formError = ref(null);
    const editingDriverId = ref(null);
    const driverToDelete = ref(null);
    const form = ref({ employeeNumber: "", firstName: "", lastName: "", password: "" });

    const tableColumns = [
      { key: "employeeNumber", label: "Employee #", sortable: true },
      { key: "name", label: "Name", sortable: true },
      { key: "active", label: "Status", sortable: true, align: "center" },
    ];

    const fetchDrivers = async () => {
      isLoading.value = true;
      try {
        const response = await axios.get(`${apiUrl}/drivers/${store.state.currentSite}`);
        filteredDrivers.value = response.data;
        fetchError.value = null;
      } catch (error) {
        console.error("Error fetching drivers:", error);
        fetchError.value = "Failed to load drivers. Please try again.";
      } finally {
        isLoading.value = false;
      }
    };

    const openCreateModal = () => { isEditing.value = false; editingDriverId.value = null; formError.value = null; showPin.value = false; form.value = { employeeNumber: "", firstName: "", lastName: "", password: "" }; showModal.value = true; };
    const openEditModal = (d) => { isEditing.value = true; editingDriverId.value = d.id; formError.value = null; showPin.value = false; form.value = { employeeNumber: d.employeeNumber, firstName: d.firstName, lastName: d.lastName, password: "" }; showModal.value = true; };
    const closeModal = () => { showModal.value = false; formError.value = null; };

    const submitDriver = async () => {
      if (isSubmitting.value) return;
      if (form.value.password && form.value.password.length < 4) { formError.value = "PIN must be at least 4 characters."; return; }
      isSubmitting.value = true; formError.value = null;
      try {
        if (isEditing.value) {
          const payload = { firstName: form.value.firstName, lastName: form.value.lastName };
          if (form.value.password) payload.password = form.value.password;
          await axios.put(`${apiUrl}/drivers/${editingDriverId.value}`, payload);
        } else {
          await axios.post(`${apiUrl}/drivers`, { employeeNumber: form.value.employeeNumber, password: form.value.password, firstName: form.value.firstName, lastName: form.value.lastName, siteId: store.state.currentSite, active: true });
        }
        showToast("success", isEditing.value ? "Driver updated" : "Driver added");
        closeModal(); await fetchDrivers();
      } catch (error) {
        formError.value = error.response?.status === 400 ? (error.response.data.detail || "Validation error") : "An error occurred.";
      } finally { isSubmitting.value = false; }
    };

    const toggleActive = async (d) => {
      // The UI treats a missing `active` field as Active, so compute the new
      // state from the same predicate (legacy rows would otherwise no-op).
      const activating = d.active === false;
      try {
        await axios.put(`${apiUrl}/drivers/${d.id}`, { active: activating });
        showToast("success", activating ? "Driver activated" : "Driver deactivated");
        await fetchDrivers();
      } catch (e) {
        console.error(e);
        showToast("error", `Failed to ${activating ? "activate" : "deactivate"} driver. Please try again.`);
      }
    };
    const confirmDeleteDriver = (d) => { driverToDelete.value = d; showDeleteConfirm.value = true; };
    const deleteDriver = async () => {
      if (!driverToDelete.value) return;
      isSubmitting.value = true;
      try {
        await axios.delete(`${apiUrl}/drivers/${driverToDelete.value.id}`);
        showDeleteConfirm.value = false;
        driverToDelete.value = null;
        showToast("success", "Driver deleted");
        await fetchDrivers();
      } catch (e) {
        console.error(e);
        showToast("error", "Failed to delete driver. Please try again.");
      } finally { isSubmitting.value = false; }
    };

    onMounted(fetchDrivers);
    return { filteredDrivers, isLoading, fetchError, showPin, showModal, showDeleteConfirm, isEditing, isSubmitting, formError, form, tableColumns, driverToDelete, openCreateModal, openEditModal, closeModal, submitDriver, toggleActive, confirmDeleteDriver, deleteDriver, fetchDrivers };
  },
};
</script>

<style scoped>
@media (prefers-reduced-motion: no-preference) {
  .drivers-page > * { animation: fadeSlideIn 0.35s cubic-bezier(0.4, 0, 0.2, 1) both; }
  .drivers-page > *:nth-child(1) { animation-delay: 0s; }
  .drivers-page > *:nth-child(2) { animation-delay: 0.06s; }
}
@keyframes fadeSlideIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
</style>
